import { cn } from "@/utils";
import type { NavIcon } from "@/backoffice/components/layouts/backofficeNavItems";
import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";

type PopoverChild = {
  key: string;
  path: string;
  title: string;
};

type NavPopoverProps = {
  icon: NavIcon;
  title: string;
  children: PopoverChild[];
  isSelected: boolean;
  onChildClick?: () => void;
};

export const NavPopover = ({
  icon: IconN,
  title,
  children,
  isSelected,
  onChildClick,
}: NavPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverTop, setPopoverTop] = useState(0);
  const location = useLocation();

  const isChildActive = children.some(
    child => location.pathname === child.path
  );
  const isHighlighted = isSelected || isChildActive;

  const openPopover = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPopoverTop(rect.top);
    }
    setIsOpen(true);
  };

  const closePopover = () => setIsOpen(false);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePopover();
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        closePopover();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleMouseDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [isOpen]);

  useEffect(() => {
    closePopover();
  }, [location.pathname]);

  return (
    <div className="relative w-full">
      <button
        ref={triggerRef}
        onClick={openPopover}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={title}
        className={cn(
          "text-hermes-muted-fg hover:bg-hermes-muted hover:text-hermes-fg group hermes-focus-ring flex h-10 w-full cursor-pointer items-center rounded-xl px-3.5 transition-colors duration-150 motion-reduce:transition-none",
          {
            "bg-hermes-accent-soft text-hermes-accent font-semibold":
              isHighlighted,
          }
        )}
      >
        <IconN
          size={18}
          className={cn(
            "transition-transform duration-200 ease-out group-hover:scale-110 group-hover:-rotate-12 motion-reduce:transition-none",
            isHighlighted && "fill-current/20"
          )}
        />
      </button>

      {isOpen && (
        <div
          ref={popoverRef}
          role="menu"
          style={{ top: popoverTop }}
          className="hermes animate-hermes-swap-in bg-hermes-bg border-hermes-border fixed left-[84px] z-50 min-w-[200px] rounded-2xl border p-1.5 shadow-[0_12px_40px_-12px_rgb(0_0_0/.25)]"
        >
          <div className="border-hermes-border mb-1 flex items-center justify-between border-b px-2 pb-1.5">
            <span className="text-hermes-fg text-sm font-semibold">
              {title}
            </span>
            <button
              onClick={closePopover}
              className="text-hermes-muted-fg hover:bg-hermes-muted hover:text-hermes-fg rounded-md p-0.5 transition-colors duration-150"
              aria-label="Fechar menu"
            >
              <X size={14} />
            </button>
          </div>
          <div className="flex flex-col gap-0.5">
            {children.map(({ key, path, title: childTitle }) => {
              const isActive = location.pathname === path;
              return (
                <Link
                  key={key}
                  to={path}
                  role="menuitem"
                  onClick={() => {
                    closePopover();
                    onChildClick?.();
                  }}
                  className={cn(
                    "text-hermes-muted-fg hover:bg-hermes-muted hover:text-hermes-fg rounded-xl px-3 py-2 text-sm transition-colors duration-150 motion-reduce:transition-none",
                    {
                      "bg-hermes-accent-soft text-hermes-accent font-semibold":
                        isActive,
                    }
                  )}
                >
                  {childTitle}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
